import { FC, useCallback } from 'react';
import classnames from 'classnames';

import { DisabledContext } from '@hh.ru/magritte-common-use-disabled';
import { Action } from '@hh.ru/magritte-ui-action';
import { BannerProps } from '@hh.ru/magritte-ui-banner/types';
import { ButtonStack } from '@hh.ru/magritte-ui-button-stack';
import { Card } from '@hh.ru/magritte-ui-card';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';

import styles from './banner.less';

const BORDER_RADIUS_MAP = {
    small: 24,
    medium: 28,
    large: 32,
} as const;

export const Banner: FC<BannerProps> = ({
    stretched = false,
    showBorder = false,
    style,
    disabled = false,
    styleClose = 'neutral',
    content,
    showClose: _showClose = true,
    visible = true,
    onClose,
    title,
    buttonBase,
    buttonAdditional,
    'aria-label-close': ariaLabelClose,
    'data-qa': dataQa,
    buttonsOrientation: _buttonsOrientation = 'auto',
    layout = 'vertical',
    imageUrl,
    size = 'small',
    imageSizeMode = 'contain',
}) => {
    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const hasButtons = !!buttonBase || !!buttonAdditional;
    const isHorizontal = layout === 'horizontal' && hasButtons;
    const buttonsOrientation = isHorizontal ? 'auto' : _buttonsOrientation;
    const withImage = !!imageUrl && !isHorizontal;
    const showClose = _showClose && !isHorizontal;

    const cardProps = {
        stretched,
        showBorder,
        padding: 0,
        borderRadius: BORDER_RADIUS_MAP[size],
        'data-qa': dataQa,
    } as const;

    if (style !== undefined) {
        Object.assign(cardProps, { style });
    }

    return (
        <>
            {visible && (
                <DisabledContext.Provider value={disabled}>
                    <Card {...cardProps}>
                        <div
                            className={classnames(styles.bannerLayoutContainer, styles[`size-${size}`], {
                                [styles.bannerLayoutHorizontal]: isHorizontal,
                                [styles.withImage]: withImage,
                                [styles.buttonsOrientationVertical]: buttonsOrientation === 'vertical' && hasButtons,
                            })}
                        >
                            <div className={classnames(styles.bannerTextAndButtons)}>
                                <div
                                    className={classnames(styles.bannerContent, {
                                        [styles.bannerContentPadding]: showClose,
                                    })}
                                >
                                    {!!title && <div className={styles.bannerTitlePadding}>{title}</div>}
                                    {content}
                                </div>
                                {hasButtons && (
                                    <div className={styles.buttonsContainer}>
                                        <ButtonStack orientation={buttonsOrientation} data-qa="banner-buttons-stack">
                                            {buttonBase}
                                            {buttonAdditional}
                                        </ButtonStack>
                                    </div>
                                )}
                            </div>
                            {withImage && (
                                <div
                                    className={classnames(styles.bannerImage, {
                                        [styles.coverImage]: imageSizeMode === 'cover',
                                    })}
                                    style={{ backgroundImage: `url(${imageUrl})` }}
                                    data-qa="banner-image"
                                />
                            )}
                            {showClose && !isHorizontal && (
                                <div className={styles.bannerClose}>
                                    <Action
                                        aria-label={ariaLabelClose}
                                        icon={CrossOutlinedSize24}
                                        mode="secondary"
                                        onClick={handleClose}
                                        style={styleClose}
                                        data-qa="banner-close"
                                    />
                                </div>
                            )}
                        </div>
                    </Card>
                </DisabledContext.Provider>
            )}
        </>
    );
};
